import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { setLanguage, useI18n } from "../shared/i18n";
import { clearMenusStorage } from "../menu-cache";

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Español",
  ca: "Català",
  fr: "Français",
  it: "Italiano",
  de: "Deutsch",
  pt: "Português",
  nl: "Nederlands",
  ar: "العربية",
  zh: "中文",
};

type LanguageSelectorProps = {
  compact?: boolean;
  dark?: boolean;
  variant?: "grid" | "dropdown";
};

export default function LanguageSelector({
  compact = false,
  dark = false,
  variant = "grid",
}: LanguageSelectorProps) {
  const { language, supportedLanguages } = useI18n();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const selectLanguage = async (code: string) => {
    clearMenusStorage(code);
    await setLanguage(code);
    window.dispatchEvent(new CustomEvent("menus:refresh-requested", { detail: { language: code } }));
  };

  if (!supportedLanguages.length) return null;

  useEffect(() => {
    if (variant !== "dropdown" || !open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
    };
  }, [open, variant]);

  if (variant === "dropdown") {
    const currentLabel = LANGUAGE_LABELS[language] || language.toUpperCase();

    return (
      <div
        ref={dropdownRef}
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="listbox"
          aria-expanded={open}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            borderRadius: 999,
            border: `1px solid ${dark ? "rgba(148, 163, 184, 0.28)" : "rgba(148, 163, 184, 0.4)"}`,
            background: dark ? "rgba(15, 23, 42, 0.56)" : "rgba(255, 255, 255, 0.78)",
            color: dark ? "rgba(255,255,255,0.92)" : "#0f172a",
            padding: "0.65rem 0.95rem",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            boxShadow: dark ? "0 10px 24px rgba(0, 0, 0, 0.16)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <Globe size={16} strokeWidth={2.1} />
          <span>{currentLabel}</span>
          <ChevronDown
            size={15}
            strokeWidth={2.1}
            style={{
              opacity: 0.8,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 160ms ease",
            }}
          />
        </button>

        {open ? (
          <div
            role="listbox"
            aria-label="Language selector"
            style={{
              position: "absolute",
              top: "calc(100% + 0.6rem)",
              right: 0,
              minWidth: 220,
              padding: "0.45rem",
              borderRadius: 18,
              border: `1px solid ${dark ? "rgba(148, 163, 184, 0.22)" : "rgba(148, 163, 184, 0.35)"}`,
              background: dark ? "rgba(15, 23, 42, 0.96)" : "rgba(255,255,255,0.96)",
              boxShadow: dark ? "0 24px 48px rgba(0, 0, 0, 0.34)" : "0 24px 48px rgba(15, 23, 42, 0.14)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              zIndex: 20,
            }}
          >
            {supportedLanguages.map((code) => {
              const isActive = code === language;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void selectLanguage(code);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    border: "none",
                    borderRadius: 14,
                    background: isActive
                      ? dark
                        ? "rgba(37, 99, 235, 0.18)"
                        : "rgba(219, 234, 254, 0.95)"
                      : "transparent",
                    color: dark ? "#fff" : "#0f172a",
                    padding: "0.8rem 0.9rem",
                    fontSize: 14,
                    fontWeight: isActive ? 800 : 600,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span>{LANGUAGE_LABELS[code] || code.toUpperCase()}</span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      opacity: isActive ? 1 : 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {code}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: compact ? 8 : 10,
        justifyContent: "center",
      }}
    >
      {supportedLanguages.map((code) => {
        const isActive = code === language;
        return (
          <button
            key={code}
            type="button"
            onClick={() => void selectLanguage(code)}
            style={{
              borderRadius: 999,
              border: isActive
                ? `1px solid ${dark ? "rgba(147, 197, 253, 0.9)" : "rgba(30, 64, 175, 0.8)"}`
                : `1px solid ${dark ? "rgba(148, 163, 184, 0.35)" : "rgba(148, 163, 184, 0.45)"}`,
              background: isActive
                ? dark
                  ? "rgba(37, 99, 235, 0.3)"
                  : "rgba(219, 234, 254, 0.95)"
                : dark
                  ? "rgba(15, 23, 42, 0.7)"
                  : "rgba(255,255,255,0.82)",
              color: dark ? "#fff" : "#0f172a",
              padding: compact ? "8px 12px" : "10px 14px",
              fontSize: compact ? 14 : 15,
              fontWeight: isActive ? 800 : 600,
              cursor: "pointer",
              minWidth: compact ? 72 : 88,
            }}
          >
            {LANGUAGE_LABELS[code] || code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
